// Reads the date a photo or video was taken from inside the file, without
// decoding it:
//   - JPEG and HEIC/HEIF photos: EXIF DateTimeOriginal (+ OffsetTimeOriginal)
//   - MP4 / MOV / M4V / M4A: Apple's com.apple.quicktime.creationdate, else
//     the movie header (mvhd) creation time
//
// Returns a calendar date (YYYY-MM-DD). When the file records an absolute
// moment (a UTC timestamp or a wall time with its zone) the date is taken in
// the viewer's time zone, so photos and videos from the same afternoon land on
// the same day; a bare camera wall time is used as-is.

export type ByteSource = {
  size: number;
  read: (offset: number, length: number) => Promise<DataView>;
};

export const blobSource = (blob: Blob): ByteSource => ({
  size: blob.size,
  read: async (offset, length) => {
    const end = Math.min(blob.size, offset + length);
    const buffer = await blob
      .slice(offset, Math.max(offset, end))
      .arrayBuffer();
    return new DataView(buffer);
  },
});

const JPEG_HEAD_BYTES = 256 * 1024;
const MAX_MOOV_BYTES = 64 * 1024 * 1024;
const MAX_META_BYTES = 4 * 1024 * 1024;
const MAC_EPOCH_OFFSET_SECONDS = 2082844800; // 1904-01-01 → 1970-01-01

const pad = (n: number) => String(n).padStart(2, '0');

export const localDateOf = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const isPlausibleDay = (
  year: number,
  month: number,
  day: number,
  minYear = 1900,
) =>
  year >= minYear &&
  year <= new Date().getFullYear() + 1 &&
  month >= 1 &&
  month <= 12 &&
  day >= 1 &&
  day <= 31;

// Container timestamps of 0 (1904) or the Unix epoch mean "not set".
const instantToLocalDate = (ms: number): string | null => {
  if (!Number.isFinite(ms)) return null;
  const date = new Date(ms);
  if (date.getUTCFullYear() < 1971) return null;
  if (date.getUTCFullYear() > new Date().getFullYear() + 1) return null;
  return localDateOf(date);
};

const fourcc = (view: DataView, at: number) =>
  String.fromCharCode(
    view.getUint8(at),
    view.getUint8(at + 1),
    view.getUint8(at + 2),
    view.getUint8(at + 3),
  );

const uint64 = (view: DataView, at: number) =>
  view.getUint32(at) * 2 ** 32 + view.getUint32(at + 4);

const text = (view: DataView, at: number, length: number) => {
  let out = '';
  for (let i = 0; i < length && at + i < view.byteLength; i += 1) {
    const code = view.getUint8(at + i);
    if (code === 0) break;
    out += String.fromCharCode(code);
  }
  return out;
};

// ---------------------------------------------------------------------------
// EXIF (TIFF structure), shared by JPEG and HEIF
// ---------------------------------------------------------------------------

const EXIF_WALL_TIME = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/;
const ZONE = /^([+-])(\d{2}):?(\d{2})$/;

export const exifDateToLocalDate = (
  value?: string | number,
  offset?: string | number,
): string | null => {
  if (typeof value !== 'string') return null;
  const match = EXIF_WALL_TIME.exec(value.trim());
  if (!match) return null;
  const [year, month, day, hour, minute, second] = match.slice(1).map(Number);
  if (!isPlausibleDay(year, month, day)) return null;

  const zone = typeof offset === 'string' ? ZONE.exec(offset.trim()) : null;
  if (zone) {
    const sign = zone[1] === '-' ? -1 : 1;
    const minutes = sign * (Number(zone[2]) * 60 + Number(zone[3]));
    const utc = Date.UTC(year, month - 1, day, hour, minute, second);
    return instantToLocalDate(utc - minutes * 60000);
  }
  return `${year}-${pad(month)}-${pad(day)}`;
};

const TAG_DATE_TIME = 0x0132;
const TAG_EXIF_IFD = 0x8769;
const TAG_DATE_TIME_ORIGINAL = 0x9003;
const TAG_DATE_TIME_DIGITIZED = 0x9004;
const TAG_OFFSET_TIME = 0x9010;
const TAG_OFFSET_TIME_ORIGINAL = 0x9011;
const TAG_OFFSET_TIME_DIGITIZED = 0x9012;

const readTiffDate = (view: DataView, start: number): string | null => {
  if (start + 8 > view.byteLength) return null;
  const order = view.getUint16(start);
  const little = order === 0x4949;
  if (!little && order !== 0x4d4d) return null;
  const u16 = (at: number) => view.getUint16(start + at, little);
  const u32 = (at: number) => view.getUint32(start + at, little);
  if (u16(2) !== 42) return null;

  const ascii = (entry: number, count: number) => {
    const at = count <= 4 ? entry + 8 : u32(entry + 8);
    return text(view, start + at, count);
  };

  const readIfd = (ifdOffset: number) => {
    const tags = new Map<number, string | number>();
    if (start + ifdOffset + 2 > view.byteLength) return tags;
    const entries = u16(ifdOffset);
    for (let i = 0; i < entries; i += 1) {
      const entry = ifdOffset + 2 + i * 12;
      if (start + entry + 12 > view.byteLength) break;
      const tag = u16(entry);
      const type = u16(entry + 2);
      const count = u32(entry + 4);
      if (type === 2) tags.set(tag, ascii(entry, count));
      else if (type === 4 || type === 13) tags.set(tag, u32(entry + 8));
    }
    return tags;
  };

  const ifd0 = readIfd(u32(4));
  const exifPointer = ifd0.get(TAG_EXIF_IFD);
  const exif =
    typeof exifPointer === 'number' ? readIfd(exifPointer) : new Map();

  const candidates: [number, number][] = [
    [TAG_DATE_TIME_ORIGINAL, TAG_OFFSET_TIME_ORIGINAL],
    [TAG_DATE_TIME_DIGITIZED, TAG_OFFSET_TIME_DIGITIZED],
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const [dateTag, offsetTag] = candidates[i];
    const date = exifDateToLocalDate(
      exif.get(dateTag),
      exif.get(offsetTag) ?? exif.get(TAG_OFFSET_TIME),
    );
    if (date) return date;
  }
  return exifDateToLocalDate(
    ifd0.get(TAG_DATE_TIME),
    exif.get(TAG_OFFSET_TIME),
  );
};

const isExifHeader = (view: DataView, at: number) =>
  at + 6 <= view.byteLength &&
  view.getUint32(at) === 0x45786966 && // "Exif"
  view.getUint16(at + 4) === 0;

// ---------------------------------------------------------------------------
// JPEG
// ---------------------------------------------------------------------------

const readJpegDate = async (source: ByteSource): Promise<string | null> => {
  const view = await source.read(0, JPEG_HEAD_BYTES);
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;
  let pos = 2;
  while (pos + 4 <= view.byteLength) {
    if (view.getUint8(pos) !== 0xff) return null;
    const marker = view.getUint8(pos + 1);
    if (marker === 0xff) {
      pos += 1; // fill byte
    } else if (marker === 0xd9 || marker === 0xda) {
      return null; // end of image / start of scan: no metadata after this
    } else if ((marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) {
      pos += 2; // markers without a length
    } else {
      const length = view.getUint16(pos + 2);
      if (marker === 0xe1 && isExifHeader(view, pos + 4)) {
        const date = readTiffDate(view, pos + 10);
        if (date) return date;
      }
      pos += 2 + length;
    }
  }
  return null;
};

// ---------------------------------------------------------------------------
// ISO base media (MP4, MOV, HEIF)
// ---------------------------------------------------------------------------

type Box = { type: string; start: number; headerSize: number; size: number };

const boxAt = (view: DataView, pos: number, limit: number): Box | null => {
  if (pos + 8 > limit) return null;
  let size = view.getUint32(pos);
  const type = fourcc(view, pos + 4);
  let headerSize = 8;
  if (size === 1) {
    if (pos + 16 > limit) return null;
    size = uint64(view, pos + 8);
    headerSize = 16;
  } else if (size === 0) {
    size = limit - pos;
  }
  if (size < headerSize) return null;
  return { type, start: pos, headerSize, size };
};

const childBoxes = (view: DataView, from: number, to: number): Box[] => {
  const boxes: Box[] = [];
  let pos = from;
  const end = Math.min(to, view.byteLength);
  while (pos + 8 <= end) {
    const box = boxAt(view, pos, end);
    if (!box || box.start + box.size > end) break;
    boxes.push(box);
    pos += box.size;
  }
  return boxes;
};

// Walks the file's top-level boxes reading only their headers, so a moov
// stored after gigabytes of media data costs a handful of small reads.
const topLevelBoxes = async (source: ByteSource, max = 64): Promise<Box[]> => {
  const boxes: Box[] = [];
  let pos = 0;
  while (pos + 8 <= source.size && boxes.length < max) {
    // eslint-disable-next-line no-await-in-loop
    const header = await source.read(pos, 16);
    const box = boxAt(header, 0, header.byteLength);
    if (!box) break;
    // A size of 0 means "runs to the end of the file".
    const size = header.getUint32(0) === 0 ? source.size - pos : box.size;
    boxes.push({ ...box, start: pos, size });
    pos += size;
  }
  return boxes;
};

const isoToLocalDate = (value: string): string | null => {
  // Apple writes "2023-05-26T12:54:26-0600"; make the zone ISO 8601.
  const normalized = value.trim().replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  if (!/^\d{4}-\d{2}-\d{2}T/.test(normalized)) return null;
  return instantToLocalDate(Date.parse(normalized));
};

const APPLE_CREATION_DATE = 'com.apple.quicktime.creationdate';

const appleCreationDate = (view: DataView, moovChildren: Box[]) => {
  const meta = moovChildren.find((box) => box.type === 'meta');
  if (!meta) return null;
  const body = meta.start + meta.headerSize;
  const end = meta.start + meta.size;
  // QuickTime's meta has no version/flags; ISO's does.
  const skip = body + 8 <= end && fourcc(view, body + 4) === 'hdlr' ? 0 : 4;
  const children = childBoxes(view, body + skip, end);
  const keys = children.find((box) => box.type === 'keys');
  const ilst = children.find((box) => box.type === 'ilst');
  if (!keys || !ilst) return null;

  let pos = keys.start + keys.headerSize + 4;
  const keysEnd = keys.start + keys.size;
  const count = view.getUint32(pos);
  pos += 4;
  let wanted = -1;
  for (let index = 1; index <= count && pos + 8 <= keysEnd; index += 1) {
    const keySize = view.getUint32(pos);
    if (keySize < 8) break;
    if (text(view, pos + 8, keySize - 8) === APPLE_CREATION_DATE) {
      wanted = index;
      break;
    }
    pos += keySize;
  }
  if (wanted < 0) return null;

  const items = childBoxes(
    view,
    ilst.start + ilst.headerSize,
    ilst.start + ilst.size,
  );
  const item = items.find((box) => view.getUint32(box.start + 4) === wanted);
  if (!item) return null;
  const data = childBoxes(
    view,
    item.start + item.headerSize,
    item.start + item.size,
  ).find((box) => box.type === 'data');
  if (!data || data.size < 16) return null;
  return isoToLocalDate(text(view, data.start + 16, data.size - 16));
};

const readMovieDate = async (source: ByteSource): Promise<string | null> => {
  const moov = (await topLevelBoxes(source)).find((box) => box.type === 'moov');
  if (!moov || moov.size > MAX_MOOV_BYTES) return null;
  const view = await source.read(moov.start, moov.size);
  const children = childBoxes(view, moov.headerSize, moov.size);

  const apple = appleCreationDate(view, children);
  if (apple) return apple;

  const mvhd = children.find((box) => box.type === 'mvhd');
  if (!mvhd) return null;
  const at = mvhd.start + mvhd.headerSize;
  const version = view.getUint8(at);
  const seconds = version === 1 ? uint64(view, at + 4) : view.getUint32(at + 4);
  if (!seconds) return null;
  return instantToLocalDate((seconds - MAC_EPOCH_OFFSET_SECONDS) * 1000);
};

const readHeifDate = async (source: ByteSource): Promise<string | null> => {
  const meta = (await topLevelBoxes(source, 16)).find(
    (box) => box.type === 'meta',
  );
  if (!meta || meta.size > MAX_META_BYTES) return null;
  const view = await source.read(meta.start, meta.size);
  const children = childBoxes(view, meta.headerSize + 4, meta.size);
  const iinf = children.find((box) => box.type === 'iinf');
  const iloc = children.find((box) => box.type === 'iloc');
  if (!iinf || !iloc) return null;

  // Which item holds the EXIF block?
  const iinfVersion = view.getUint8(iinf.start + iinf.headerSize);
  const entriesStart =
    iinf.start + iinf.headerSize + 4 + (iinfVersion === 0 ? 2 : 4);
  let exifItem = -1;
  childBoxes(view, entriesStart, iinf.start + iinf.size).some((infe) => {
    if (infe.type !== 'infe') return false;
    const at = infe.start + infe.headerSize;
    const version = view.getUint8(at);
    if (version < 2) return false;
    const id = version === 2 ? view.getUint16(at + 4) : view.getUint32(at + 4);
    const typeAt = at + 4 + (version === 2 ? 2 : 4) + 2;
    if (fourcc(view, typeAt) !== 'Exif') return false;
    exifItem = id;
    return true;
  });
  if (exifItem < 0) return null;

  // Where is it stored?
  const ilocAt = iloc.start + iloc.headerSize;
  const version = view.getUint8(ilocAt);
  let pos = ilocAt + 4;
  const readUint = (bytes: number) => {
    let value = 0;
    for (let i = 0; i < bytes; i += 1)
      value = value * 256 + view.getUint8(pos + i);
    pos += bytes;
    return value;
  };
  const sizes = readUint(1);
  const moreSizes = readUint(1);
  const offsetSize = sizes >> 4; // eslint-disable-line no-bitwise
  const lengthSize = sizes & 15; // eslint-disable-line no-bitwise
  const baseOffsetSize = moreSizes >> 4; // eslint-disable-line no-bitwise
  const indexSize = version === 1 || version === 2 ? moreSizes & 15 : 0; // eslint-disable-line no-bitwise
  const itemCount = readUint(version < 2 ? 2 : 4);
  for (let i = 0; i < itemCount; i += 1) {
    const id = readUint(version < 2 ? 2 : 4);
    const method = version === 1 || version === 2 ? readUint(2) & 15 : 0; // eslint-disable-line no-bitwise
    readUint(2); // data_reference_index
    const baseOffset = readUint(baseOffsetSize);
    const extents = readUint(2);
    let first: { offset: number; length: number } | null = null;
    for (let e = 0; e < extents; e += 1) {
      if (indexSize) readUint(indexSize);
      const offset = readUint(offsetSize);
      const length = readUint(lengthSize);
      if (!first) first = { offset, length };
    }
    if (id === exifItem && first && method === 0) {
      // eslint-disable-next-line no-await-in-loop
      const exif = await source.read(
        baseOffset + first.offset,
        Math.min(first.length, JPEG_HEAD_BYTES),
      );
      if (exif.byteLength < 8) return null;
      return readTiffDate(exif, 4 + exif.getUint32(0));
    }
  }
  return null;
};

/**
 * The date the photo or video was taken, read from the file's own metadata,
 * or null when the file doesn't record one (or isn't a format we read).
 */
export const readCaptureDate = async (
  blob: Blob,
  mimeType: string,
): Promise<string | null> => {
  try {
    const source = blobSource(blob);
    if (mimeType === 'image/jpeg') return await readJpegDate(source);
    if (mimeType === 'image/heic' || mimeType === 'image/heif') {
      return await readHeifDate(source);
    }
    if (
      mimeType.startsWith('video/') ||
      mimeType === 'audio/mp4' ||
      mimeType === 'audio/x-m4a'
    ) {
      return await readMovieDate(source);
    }
    return null;
  } catch {
    return null;
  }
};
